import { ZfBanner, ZfFilesUpload, ZfIcon, ZfIconButton, ZfStack, ZfToggle } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddGenericDocuments, useDeleteAppendixDocument, useDownloadAppendixDocument } from '../../api';
import type { ClientPackageAppendixDocumentDto, ClientPackageDto } from '../../api/generated/client';
import { CLIENT_PACKAGE_DOCUMENT_TYPE } from '../../config/constants';
import { getSplitClientPackageAppendix, getUpdatedAppendixDocuments } from '../../utils/clientPackageUtils';
import { ClientPackageCheckboxItem } from './ClientPackageCheckboxItem';

interface IProps {
    clientPackage: ClientPackageDto;
    disabled?: boolean;
    updateClientPackage: (updatedPackage: Partial<ClientPackageDto>) => void;
}

export const ClientPackageGenericDocumentsSelector: FunctionComponent<IProps> = ({ clientPackage, disabled, updateClientPackage }) => {
    const { t } = useTranslation();

    const downloadAppendixDocument = useDownloadAppendixDocument();
    const deleteAppendixDocument = useDeleteAppendixDocument();
    const addGenericDocuments = useAddGenericDocuments();

    const onAppendixSelected = useCallback(
        (appendix: ClientPackageAppendixDocumentDto, selected: boolean) => {
            updateClientPackage(getUpdatedAppendixDocuments(clientPackage, appendix, selected));
        },
        [clientPackage, updateClientPackage],
    );

    const onUploadNewGenericDocuments = async (files: File[]) => {
        const newGenericDocuments = await addGenericDocuments.mutateAsync({ packageId: clientPackage.id, files });
        updateClientPackage({ ...clientPackage, clientPackageAppendixDocuments: [...clientPackage.clientPackageAppendixDocuments, ...newGenericDocuments] });
    };

    const onDeleteGenericDocument = async (genericDocumentToDelete: ClientPackageAppendixDocumentDto) => {
        await deleteAppendixDocument.mutateAsync(genericDocumentToDelete.documentId);
        const { appendixDocuments } = getSplitClientPackageAppendix(clientPackage, genericDocumentToDelete.documentId);
        updateClientPackage({ ...clientPackage, clientPackageAppendixDocuments: appendixDocuments });
    };

    const sortedGenericDocuments = [...clientPackage.clientPackageAppendixDocuments]
        .filter((appendix) => appendix.type === 'OTHER')
        .sort((a, b) => a.documentId - b.documentId);

    return (
        <div className="client-package-editor__selector-panel">
            <ZfToggle
                label={t('clientPackageGenericDocumentsMasterToggle')}
                disabled={disabled}
                checked={clientPackage.genericDocumentsEnabled}
                onToggleChange={(event: CustomEvent<boolean>) => updateClientPackage({ genericDocumentsEnabled: event.detail })}
            />
            <div className="client-package-editor__selector-panel__content">
                {clientPackage.genericDocumentsEnabled && (
                    <>
                        {!disabled && (
                            <ZfBanner description={t('clientPackageGenericDocumentsInfoMessage')} dismissible={false}>
                                <ZfIcon name="info" size="medium" slot="leading" />
                            </ZfBanner>
                        )}
                        <div className="client-package-editor__selector-panel__documents">
                            {sortedGenericDocuments.length
                                ? sortedGenericDocuments.map((genericDocument) => {
                                      const isDisabled =
                                          disabled || !clientPackage.genericDocumentsEnabled || !genericDocument.documentUuid || !genericDocument.filename;

                                      return (
                                          <ClientPackageCheckboxItem
                                              key={`${genericDocument.type.toString()}-${genericDocument.documentId}`}
                                              label={genericDocument.filename ?? ''}
                                              isDisabled={isDisabled}
                                              isSelected={genericDocument.selected}
                                              onChange={(isChecked) => onAppendixSelected(genericDocument, isChecked)}
                                              actions={
                                                  <ZfStack direction="row" spacing="extra-extra-extra-small" alignment="center">
                                                      <ZfIconButton
                                                          icon="download"
                                                          size="extra-small"
                                                          hierarchy="tertiary"
                                                          onClick={() => downloadAppendixDocument.mutateAsync(genericDocument.documentId)}
                                                      />
                                                      <ZfIconButton
                                                          icon="delete"
                                                          size="extra-small"
                                                          type="destructive"
                                                          hierarchy="tertiary"
                                                          disabled={isDisabled}
                                                          onClick={() => onDeleteGenericDocument(genericDocument)}
                                                      />
                                                  </ZfStack>
                                              }
                                          />
                                      );
                                  })
                                : t('clientPackageGenericDocumentsEmpty')}
                        </div>
                        {!disabled && (
                            <ZfFilesUpload
                                text={t('clientPackageGenericDocumentsActionUpload')}
                                size="small"
                                hierarchy="secondary"
                                accept={CLIENT_PACKAGE_DOCUMENT_TYPE}
                                disabled={disabled || !clientPackage.genericDocumentsEnabled}
                                onFileInputChange={(event: CustomEvent<File[]>) => onUploadNewGenericDocuments(event.detail)}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
